import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, CircleAlert, Clock, Image as ImageIcon, Pencil, Plus, ScanText, Search, Trash2, X } from 'lucide-react';

import { numberFormatter } from '../../utils/calorieLog';
import { formatQuantity } from '../../utils/dietPlanView';
import { activeFoods, foodsTotals, isUnread } from '../../utils/dietImport';

/** Where a food's numbers come from: the TACO table, Open Food Facts, or the reading's estimate (marked). */
export const SourceTag = ({ source }) => {
  const { t } = useTranslation();
  if (source === 'estimate')
    return (
      <span className="mt-di-tag mt-di-tag--estimate">
        <CircleAlert aria-hidden="true" />
        {t('pages.DietImport.tagEstimate')}
      </span>
    );
  if (source === 'taco') return <span className="mt-di-tag">TACO</span>;
  if (source === 'open_food_facts') return <span className="mt-di-tag">Open Food Facts</span>;
  return null;
};

/**
 * One meal of a plan being reviewed or built. Closed, it's a row (✓ confirmed, its number, or ✕ not read). Open, it
 * holds its name and time, the plan's own words for it, its options as tabs, its foods (each opens the food editor),
 * and the way to add a food or confirm it and go to the next. A meal the photo missed is built here, by hand.
 */
export const MealDraftCard = ({
  meal,
  number,
  isOpen,
  isHighlighted,
  isReview,
  canShowOriginal,
  onOpen,
  onChange,
  onEditFood,
  onAddFood,
  onConfirm,
  onRemove,
  onShowOriginal,
  onHover,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ix = (key, options) => t(`pages.DietImport.${key}`, options);
  const formatNumber = numberFormatter(language);
  const foods = activeFoods(meal);
  const totals = foodsTotals(foods);
  const unread = isUnread(meal);
  const displayName = meal.name.trim() || ix('mealUnnamed');

  const hoverProps = onHover ? { onMouseEnter: () => onHover(meal.id), onMouseLeave: () => onHover(null) } : {};

  if (!isOpen) {
    let sub = ix('foodsCount', { count: foods.length });
    if (meal.confirmed) sub = ix('mealConfirmed');
    else if (unread) sub = ix('mealUnreadShort');
    else if (meal.time) sub = `${meal.time} · ${sub}`;
    const flags = foods.filter((food) => food.assumed || food.source === 'estimate').length;
    return (
      <li className={`mt-di-meal${isHighlighted ? ' is-hover' : ''}`} {...hoverProps}>
        <button type="button" className="mt-di-meal__row" onClick={onOpen} aria-expanded="false">
          <span className={`mt-di-node${meal.confirmed ? ' is-done' : unread ? ' is-unread' : ''}`} aria-hidden="true">
            {meal.confirmed ? <Check /> : unread ? <X /> : number}
          </span>
          <span className="mt-di-meal__text">
            <span className="mt-di-meal__title">{displayName}</span>
            <span className={`mt-di-meal__sub${!meal.confirmed && flags ? ' is-flag' : ''}`}>
              {!meal.confirmed && flags ? ix('flagsShort', { count: flags }) : sub}
            </span>
          </span>
          {foods.length > 0 && <span className="mt-di-meal__kcal">{t('pages.CalorieTracker.ui.kcal', { kcal: formatNumber(totals.calories) })}</span>}
          <ChevronDown className="mt-di-meal__chevron" aria-hidden="true" />
        </button>
      </li>
    );
  }

  const setActive = (index) => onChange({ ...meal, active: index });
  const addOption = () => onChange({ ...meal, options: [...meal.options, { foods: [] }], active: meal.options.length, confirmed: false });
  const removeOption = () => {
    const options = meal.options.filter((_, index) => index !== meal.active);
    onChange({ ...meal, options, active: Math.max(0, meal.active - 1) });
  };

  return (
    <li className={`mt-di-meal is-open${isHighlighted ? ' is-hover' : ''}`} {...hoverProps}>
      <div className="mt-di-meal__card">
        <div className="mt-di-meal__head">
          <span className={`mt-di-node is-current`} aria-hidden="true">
            {number}
          </span>
          <input
            className="mt-di-meal__name"
            value={meal.name}
            placeholder={ix('mealNamePlaceholder')}
            aria-label={ix('mealNameLabel')}
            onChange={(event) => onChange({ ...meal, name: event.target.value })}
          />
          <span className="mt-di-meal__kcal mt-di-meal__kcal--big">{t('pages.CalorieTracker.ui.kcal', { kcal: formatNumber(totals.calories) })}</span>
        </div>
        <label className="mt-di-time">
          <Clock aria-hidden="true" />
          <span className="mt-sr-only">{ix('mealTimeLabel', { meal: displayName })}</span>
          <input type="time" value={meal.time || ''} onChange={(event) => onChange({ ...meal, time: event.target.value || null })} />
        </label>

        {isReview && meal.sourceText && (
          <p className="mt-di-quote">
            <ScanText aria-hidden="true" />
            <span>
              {ix('inPlan')} <q>{meal.sourceText}</q>
            </span>
          </p>
        )}

        {(meal.options.length > 1 || foods.length > 0) && (
          <div className="mt-di-opts" role="group" aria-label={ix('optionsLabel')}>
            {meal.options.length > 1 &&
              meal.options.map((option, index) => (
                <button key={index} type="button" aria-pressed={meal.active === index} onClick={() => setActive(index)}>
                  {ix('optionN', { n: index + 1 })}
                  <small>{formatNumber(foodsTotals(option.foods).calories)} kcal</small>
                </button>
              ))}
            <button type="button" className="mt-di-opts__add" onClick={addOption}>
              <Plus aria-hidden="true" />
              {ix('optionN', { n: meal.options.length + 1 })}
            </button>
          </div>
        )}

        {unread ? (
          <div className="mt-di-unread">
            <p className="mt-di-unread__title">{ix('mealUnreadTitle')}</p>
            <p className="mt-di-unread__text">{ix('mealUnreadText')}</p>
          </div>
        ) : foods.length === 0 ? (
          <p className="mt-di-empty-meal">{ix(meal.options.length > 1 ? 'optionEmpty' : 'mealEmpty')}</p>
        ) : (
          <ul className="mt-di-foods">
            {foods.map((food, index) => {
              const isEstimate = food.source === 'estimate';
              const quantity = formatQuantity(food.quantity);
              return (
                <li key={`${index}-${food.name}`} className={food.assumed || isEstimate ? 'is-flagged' : undefined}>
                  <button type="button" className="mt-di-food" onClick={() => onEditFood(index)} aria-label={t('pages.DietPlan.ui.editFood', { food: food.name, quantity })}>
                    <span className="mt-di-food__line">
                      <span className="mt-di-food__name">{food.name}</span> <small>{quantity}</small>
                    </span>
                    <span className="mt-di-food__kcal">
                      {isEstimate ? '≈ ' : ''}
                      {t('pages.CalorieTracker.ui.kcal', { kcal: formatNumber(food.calories) })}
                    </span>
                    <span className="mt-di-food__meta">
                      <SourceTag source={food.source} />
                      <span>{ix('foodMacros', { protein: Math.round(food.protein || 0), carbs: Math.round(food.carbs || 0), fat: Math.round(food.fat || 0) })}</span>
                    </span>
                    {food.assumed && (
                      <span className="mt-di-food__note">
                        {food.amountText
                          ? ix('assumedNote', { said: food.amountText, grams: food.grams, unit: food.unit || 'g' })
                          : ix('assumedNoteNoText', { grams: food.grams, unit: food.unit || 'g' })}
                      </span>
                    )}
                    {isEstimate && <span className="mt-di-food__note">{ix('estimateNote')}</span>}
                    <Pencil className="mt-di-food__pencil" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-di-meal__foot">
          <button type="button" className="mt-btn mt-btn--quiet" onClick={onAddFood}>
            <Search aria-hidden="true" />
            {ix('addFood')}
          </button>
          {canShowOriginal && (
            <button type="button" className="mt-btn mt-btn--quiet mt-di-phone-only" onClick={onShowOriginal}>
              <ImageIcon aria-hidden="true" />
              {ix('seeOriginal')}
            </button>
          )}
          <button type="button" className="mt-btn mt-btn--primary mt-di-meal__confirm" onClick={onConfirm} disabled={foods.length === 0 && meal.options.every((option) => option.foods.length === 0)}>
            <Check aria-hidden="true" />
            {ix(isReview ? 'confirmNext' : 'mealDone')}
          </button>
        </div>
        <div className="mt-di-meal__minor">
          {meal.options.length > 1 && (
            <button type="button" className="mt-di-link" onClick={removeOption}>
              <X aria-hidden="true" />
              {ix('removeOption', { n: meal.active + 1 })}
            </button>
          )}
          <button type="button" className="mt-di-link mt-di-link--danger" onClick={onRemove}>
            <Trash2 aria-hidden="true" />
            {ix('removeMeal')}
          </button>
        </div>
      </div>
    </li>
  );
};
